/**
 * Formats fr-FR des cartes de stats — une seule source pour « 7 327 $US »,
 * « 34,99 € », « 87,9 k$US », « 912 », « 12,5 % ».
 *
 * Règles :
 *   - milliers séparés par l'espace fine insécable (Intl fr-FR) ;
 *   - un montant entier n'affiche pas de décimales, un montant à centimes en
 *     affiche deux (comme les captures : « 7 327 $US » mais « 219,88 $US ») ;
 *   - au-delà de 10 000, un montant se compacte en « k » (« 87,9 k$US »), au
 *     delà du million en « M » — `compact: false` pour le forcer en entier.
 */
export type FormatValeur = "montant" | "nombre" | "pourcent";

const ESPACE_FINE = String.fromCharCode(0x202f);
/** Devant « k » / « M » : l'espace fine d'Almarena Neue est invisible, l'insécable se voit. */
const ESPACE_INSECABLE = String.fromCharCode(0xa0);

function chiffres(n: number, decimales: number): string {
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(n);
}

/** Décimales « naturelles » d'une valeur : 0 si entière, sinon `sinon`. */
export function decimalesDe(n: number, sinon: number): number {
  return Number.isInteger(n) ? 0 : sinon;
}

/** « 912 », « 1 234 567 », « 8,5 » (1 décimale par défaut si non entier). */
export function formatNombre(n: number, decimales?: number): string {
  if (!Number.isFinite(n)) return "—";
  return chiffres(n, decimales ?? decimalesDe(n, 1));
}

/** Le symbole tel que fr-FR l'écrit : « € », « $US », « £GB ». */
export function symboleDevise(devise: string): string {
  try {
    const part = new Intl.NumberFormat("fr-FR", { style: "currency", currency: devise })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    return part?.value ?? devise;
  } catch {
    return devise;
  }
}

export interface OptionsMontant {
  /** `"auto"` (défaut) : compacte dès 10 000. `true` : toujours. `false` : jamais. */
  compact?: boolean | "auto";
  /** Force le nombre de décimales (sinon 0 si entier, 2 sinon). */
  decimales?: number;
}

/** « 7 327 $US », « 34,99 € », « 87,9 k$US », « 1,2 M€ ». */
export function formatMontant(n: number, devise = "EUR", opts: OptionsMontant = {}): string {
  if (!Number.isFinite(n)) return "—";
  const compact = opts.compact ?? "auto";
  const abs = Math.abs(n);
  if (compact === true || (compact === "auto" && abs >= 10_000)) {
    const [diviseur, suffixe] = abs >= 1_000_000 ? [1_000_000, "M"] : [1_000, "k"];
    return `${chiffres(n / diviseur, 1)}${ESPACE_INSECABLE}${suffixe}${symboleDevise(devise)}`;
  }
  const d = opts.decimales ?? decimalesDe(n, 2);
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: devise,
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(n);
  } catch {
    return `${chiffres(n, d)}${ESPACE_FINE}${devise}`;
  }
}

/** « 12 % », « 12,5 % ». */
export function formatPourcent(n: number, decimales?: number): string {
  if (!Number.isFinite(n)) return "—";
  return `${chiffres(n, decimales ?? decimalesDe(n, 1))}${ESPACE_FINE}%`;
}

/**
 * Le format d'une carte, piloté par `format`. `decimales` sert pendant le
 * compteur animé : la valeur intermédiaire garde les décimales de la CIBLE
 * (un MRR entier ne clignote pas en centimes pendant qu'il monte).
 */
export function formatValeur(
  n: number,
  format: FormatValeur,
  devise?: string,
  decimales?: number,
  compact?: boolean | "auto",
): string {
  switch (format) {
    case "montant":
      return formatMontant(n, devise, { decimales, compact });
    case "pourcent":
      return formatPourcent(n, decimales);
    default:
      return formatNombre(n, decimales);
  }
}
